import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const features = [
  {
    icon: '🛡️',
    title: 'Genuine Profiles Only',
    desc: 'Every member on Weddyfyp goes through phone verification before their profile becomes visible. No bots, no fakes — just real people with real intentions.',
  },
  {
    icon: '🔐',
    title: 'You Control Your Privacy',
    desc: 'Your contact details stay hidden until you decide to share them. Browse confidently knowing your personal information is never exposed without your consent.',
  },
  {
    icon: '💞',
    title: 'Matched With Intention',
    desc: 'We do not believe in endless swiping. Our approach focuses on compatibility in values, lifestyle, and life goals — not just appearances.',
  },
  {
    icon: '👨‍👩‍👧',
    title: 'Family-Centered Design',
    desc: 'Built to respect the role families play in Indian marriages. Families can be involved as much or as little as the member prefers.',
  },
  {
    icon: '📲',
    title: 'Smooth on Every Device',
    desc: 'Whether you are on a phone, tablet, or desktop, Weddyfyp offers a clean and effortless experience from the very first tap.',
  },
  {
    icon: '🕊️',
    title: 'A Space Built on Trust',
    desc: 'Matrimony is one of life\'s most important decisions. We treat it with the seriousness and warmth it deserves at every step.',
  },
];

const steps = [
  {
    step: '01',
    title: 'Tell Us About Yourself',
    desc: 'Build a profile that truly reflects who you are — your personality, your background, your dreams, and what you seek in a partner.',
  },
  {
    step: '02',
    title: 'Explore With Purpose',
    desc: 'Browse profiles that genuinely match your preferences, or let our platform surface the most compatible people for you.',
  },
  {
    step: '03',
    title: 'Reach Out Safely',
    desc: 'Express interest and start conversations in a secure environment. No pressure — move at a pace that feels right for you.',
  },
  {
    step: '04',
    title: 'Begin Your Story Together',
    desc: 'When the connection feels right, take it forward. Many beautiful families have begun exactly where you are standing right now.',
  },
];

const testimonials = [
  {
    names: 'Rohit & Divya',
    place: 'Hyderabad',
    text: 'We matched on Weddyfyp and were surprised by how naturally our conversations flowed. Within four months of our first chat, both families had met and we were engaged. The platform made something that felt complicated feel completely natural.',
    emoji: '👫',
  },
  {
    names: 'Suresh & Lakshmi',
    place: 'Bengaluru',
    text: 'I had tried other platforms before and always felt overwhelmed. Weddyfyp was different — quieter, more serious. I found someone who shared my values, and today we are happily building our future together.',
    emoji: '💑',
  },
  {
    names: 'Aakash & Meenakshi',
    place: 'Chennai',
    text: 'My parents were sceptical about online matrimony. But the moment they saw how verified and transparent everything was, they were on board. Meenakshi and I are now building a home together in Chennai.',
    emoji: '👫',
  },
];

const communities = [
  'Hindu', 'Muslim', 'Christian', 'Sikh', 'Jain', 'Buddhist',
  'Telugu', 'Tamil', 'Kannada', 'Malayalam', 'Marathi', 'Bengali',
];

export default function Home() {
  return (
    <div>
      <Helmet>
        <title>Weddyfyp Matrimony - Free Matrimony App for Indians & NRIs</title>
        <meta name="description" content="Find your perfect life partner on Weddyfyp — India's trusted free matrimony app for Indians and NRIs. Verified profiles, privacy-first, family-friendly." />
        <link rel="canonical" href="https://weddyfyp.com/" />
      </Helmet>

      {/* ── Hero ── */}
      <section style={{
        minHeight: '100vh',
        background: 'linear-gradient(150deg, #fdf5f8 0%, #fdf5f8 55%, #fffdf7 100%)',
        display: 'flex', alignItems: 'center',
        padding: '110px 24px 70px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-120px', right: '-80px', width: '520px', height: '520px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(194,24,91,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-100px', width: '420px', height: '420px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(173,20,87,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '64px', alignItems: 'center' }}>

          {/* Left copy */}
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fdf5f8', border: '1px solid #f8bbd0', borderRadius: '30px', padding: '6px 18px', marginBottom: '28px' }}>
              <span>🪷</span>
              <span style={{ fontSize: '13px', color: '#C2185B', fontWeight: '600', letterSpacing: '0.3px' }}>Matrimony, Reimagined</span>
            </div>

            <h1 style={{ fontSize: 'clamp(38px, 5.5vw, 62px)', fontWeight: '700', color: '#1c1917', lineHeight: '1.12', marginBottom: '22px' }}>
              Where Every Heart<br />
              <span style={{ background: 'linear-gradient(120deg, #C2185B 20%, #AD1457 80%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Finds Its Home
              </span>
            </h1>

            <p style={{ fontSize: '17px', color: '#6b7280', lineHeight: '1.8', marginBottom: '14px', maxWidth: '490px' }}>
              Weddyfyp is built for people who take marriage seriously. We bring together verified profiles, genuine conversations, and family values — so your search feels less like a task and more like a beginning.
            </p>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '36px', fontStyle: 'italic' }}>
              Strictly for matrimonial purpose. Not a dating platform.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '44px' }}>
              <a href="https://play.google.com/store" target="_blank" rel="noreferrer" style={{
                padding: '15px 36px', borderRadius: '50px',
                background: 'linear-gradient(135deg, #C2185B, #AD1457)',
                color: '#fff', fontWeight: '700', fontSize: '16px',
                boxShadow: '0 8px 28px rgba(194,24,91,0.32)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                display: 'inline-flex', alignItems: 'center', gap: '8px',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 14px 34px rgba(194,24,91,0.42)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(194,24,91,0.32)'; }}
              >
                <span style={{ fontSize: '18px' }}>▶</span> Download the App
              </a>
              <Link to="/about" style={{
                padding: '15px 36px', borderRadius: '50px',
                border: '2px solid #e5e7eb', color: '#374151',
                fontWeight: '600', fontSize: '16px', background: '#fff',
                transition: 'border-color 0.2s, color 0.2s', display: 'inline-block',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#C2185B'; e.currentTarget.style.color = '#C2185B'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
              >
                Our Story →
              </Link>
            </div>

            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
              {[
                { icon: '✅', text: 'Phone-Verified Profiles' },
                { icon: '🔒', text: 'Private & Secure' },
                { icon: '👨‍👩‍👧', text: 'Family Friendly' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <span style={{ fontSize: '15px' }}>{item.icon}</span>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right card */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              width: '370px', maxWidth: '100%',
              background: '#fff',
              borderRadius: '28px', padding: '30px',
              boxShadow: '0 24px 64px rgba(194,24,91,0.10), 0 4px 16px rgba(0,0,0,0.04)',
              border: '1px solid rgba(194,24,91,0.08)',
              position: 'relative',
            }}>
              <div style={{ textAlign: 'center', marginBottom: '22px' }}>
                <div style={{ fontSize: '52px', marginBottom: '8px' }}>💍</div>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>Today's Curated Matches</p>
                <p style={{ fontSize: '12px', color: '#9ca3af' }}>Phone verified · Serious profiles only</p>
              </div>

              {[
                { name: 'Ananya R.', age: 26, loc: 'Hyderabad', emoji: '👩‍🦰', tag: 'New Match' },
                { name: 'Keerthana M.', age: 25, loc: 'Bengaluru', emoji: '👩', tag: 'Top Pick' },
              ].map((p, i) => (
                <div key={i} style={{
                  borderRadius: '14px', padding: '14px 16px',
                  marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px',
                  background: i === 0 ? '#fef9f9' : '#fff',
                  border: `1px solid ${i === 0 ? '#f8bbd0' : '#f3f4f6'}`,
                }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: i === 0 ? '#fce4ec' : '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{p.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>{p.name}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>{p.age} yrs · {p.loc}</div>
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', background: i === 0 ? '#fce4ec' : '#f0fdf4', color: i === 0 ? '#C2185B' : '#16a34a', border: `1px solid ${i === 0 ? '#f8bbd0' : '#bbf7d0'}` }}>
                    {p.tag}
                  </div>
                </div>
              ))}

              <div style={{ background: 'linear-gradient(135deg, #C2185B, #AD1457)', borderRadius: '12px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>📲</span>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '700', color: '#fff' }}>View Your Matches</p>
                  <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)' }}>Download the Weddyfyp app</p>
                </div>
              </div>

              <div style={{ position: 'absolute', top: '-16px', left: '-16px', background: '#fff', borderRadius: '12px', padding: '8px 14px', boxShadow: '0 6px 20px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '15px' }}>✅</span>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937' }}>100% Verified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: '88px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>WHAT MAKES US DIFFERENT</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', color: '#1c1917', marginBottom: '16px', lineHeight: '1.2' }}>
              Designed for Meaningful Unions,<br />Not Just Connections
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '540px', margin: '0 auto', lineHeight: '1.75' }}>
              Every feature of Weddyfyp is built with one goal — helping you find a life partner in the most genuine, dignified way possible.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {features.map((f, i) => (
              <div key={i} style={{
                padding: '32px 28px', borderRadius: '20px',
                border: '1px solid #f3f4f6', background: '#fff',
                transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 20px 48px rgba(194,24,91,0.08)'; e.currentTarget.style.borderColor = '#f8bbd0'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#f3f4f6'; }}
              >
                <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '18px' }}>{f.icon}</div>
                <h3 style={{ fontSize: '17px', color: '#1f2937', marginBottom: '10px', lineHeight: '1.3' }}>{f.title}</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.75' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section style={{ padding: '88px 24px', background: '#faf8f7' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>YOUR PATH FORWARD</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', color: '#1c1917' }}>Four Steps to the Rest of Your Life</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '36px' }}>
            {steps.map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '68px', height: '68px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #C2185B, #AD1457)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 10px 28px rgba(194,24,91,0.22)',
                }}>
                  <span style={{ fontSize: '18px', fontWeight: '800', color: '#fff', fontFamily: "'Playfair Display', serif" }}>{s.step}</span>
                </div>
                <h3 style={{ fontSize: '17px', color: '#1f2937', marginBottom: '10px' }}>{s.title}</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.75' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Communities ── */}
      <section style={{ padding: '70px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>FOR EVERYONE</p>
          <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 38px)', color: '#1c1917', marginBottom: '14px' }}>Every Community, Every Region</h2>
          <p style={{ fontSize: '15px', color: '#9ca3af', marginBottom: '36px' }}>We serve every religion, language, and community across India and beyond.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
            {communities.map(c => (
              <span key={c} style={{
                padding: '8px 20px', borderRadius: '30px',
                border: '1px solid #f8bbd0', background: '#fce4ec',
                color: '#C2185B', fontSize: '14px', fontWeight: '500',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.target.style.background = '#C2185B'; e.target.style.color = '#fff'; e.target.style.borderColor = '#C2185B'; }}
                onMouseLeave={e => { e.target.style.background = '#fce4ec'; e.target.style.color = '#C2185B'; e.target.style.borderColor = '#f8bbd0'; }}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={{ padding: '88px 24px', background: 'linear-gradient(150deg, #fdf5f8 0%, #fffbf3 100%)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>THEY FOUND EACH OTHER HERE</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', color: '#1c1917', marginBottom: '14px' }}>Stories That Warm the Heart</h2>
            <p style={{ fontSize: '16px', color: '#9ca3af' }}>Not every love story begins the same way. But many of the best ones begin here.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '28px' }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: '22px', padding: '34px',
                boxShadow: '0 6px 28px rgba(194,24,91,0.05)',
                border: '1px solid rgba(194,24,91,0.07)',
                display: 'flex', flexDirection: 'column', gap: '20px',
              }}>
                <div style={{ fontSize: '20px', color: '#AD1457', letterSpacing: '3px' }}>★★★★★</div>
                <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.85', fontStyle: 'italic', flex: 1 }}>
                  "{t.text}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{t.emoji}</div>
                  <div>
                    <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '15px' }}>{t.names}</div>
                    <div style={{ fontSize: '12px', color: '#C2185B', fontWeight: '500' }}>{t.place} · Married via Weddyfyp</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{
        padding: '88px 24px',
        background: 'linear-gradient(135deg, #880E4F 0%, #C2185B 45%, #AD1457 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 15% 50%, rgba(255,255,255,0.06) 0%, transparent 55%), radial-gradient(circle at 85% 50%, rgba(255,255,255,0.06) 0%, transparent 55%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: '680px', margin: '0 auto' }}>
          <div style={{ fontSize: '52px', marginBottom: '18px' }}>🌸</div>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 46px)', color: '#fff', marginBottom: '18px', lineHeight: '1.2' }}>
            The Right Person Is Out There.<br />Let's Help You Find Them.
          </h2>
          <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.82)', marginBottom: '44px', lineHeight: '1.7' }}>
            Thousands of people across India began their forever on Weddyfyp. Your story could be next.
          </p>
          <a href="https://play.google.com/store" target="_blank" rel="noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: '10px',
            padding: '18px 44px', borderRadius: '50px',
            background: '#fff', color: '#C2185B',
            fontWeight: '700', fontSize: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            transition: 'transform 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '20px' }}>▶</span> Download Weddyfyp Free
          </a>
          <p style={{ marginTop: '28px', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
            This website is strictly for matrimonial purpose only and not a dating website.
          </p>
        </div>
      </section>

    </div>
  );
}
